// import function to register Swiper custom elements
import { register } from 'swiper/element/bundle';

import { Component, effect, inject, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import * as Sentry from '@sentry/angular';
import {
  ActionSheetController,
  IonApp,
  IonRouterOutlet,
  ModalController,
  Platform,
  PopoverController,
  ToastController,
} from '@ionic/angular/standalone';

import { environment } from '@environments/environment';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { balanceKeys, notificationKeys, supportTicketKeys } from '@shared/stores';

import { NetworkService, StorageService } from '@movilidad/app-core';
import { SavedCardService } from '@core/services/saved-card/saved-card.service';
import { TextScaleService } from '@core/services/text-scale/text-scale.service';
import { AppUpdateService } from './services/app-update.service';
import { ForceUpdateService } from './services/force-update.service';
import { AuthService } from './services/auth.service';
import { EventService } from './services/event.service';
import { LoadingService } from './services/loading.service';
import { NotificationService } from './services/notification.service';
import { StatusBarService } from './services/status-bar.service';
import { addIcons } from 'ionicons';
import { cloudOfflineOutline } from 'ionicons/icons';

// register Swiper custom elements
register();

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  imports: [IonRouterOutlet, IonApp],
  styles: [
    `
      .dev-banner {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 99999;
        background: rgba(255, 107, 0, 0.9);
        color: #fff;
        text-align: center;
        font-size: 8px;
        font-weight: 700;
        letter-spacing: 2px;
        pointer-events: none;
      }
    `,
  ],
})
export class AppComponent implements OnInit {
  isDev = !environment.production;
  private platform = inject(Platform);
  private auth = inject(AuthService);
  private modalCtrl = inject(ModalController);
  private actionSheetCtrl = inject(ActionSheetController);
  private popoverCtrl = inject(PopoverController);
  private router = inject(Router);
  private toastController = inject(ToastController);
  private eventService = inject(EventService);
  private loadingService = inject(LoadingService);
  private appUpdateService = inject(AppUpdateService);
  private forceUpdate = inject(ForceUpdateService);
  private network = inject(NetworkService);
  private statusBarService = inject(StatusBarService);
  private notificationService = inject(NotificationService);
  private storage = inject(StorageService);
  private queryClient = inject(QueryClient);
  private textScale = inject(TextScaleService);
  private savedCards = inject(SavedCardService);

  swipe = true;
  private disconnectedToast: HTMLIonToastElement | null = null;

  // set up hardware back button event.
  lastTimeBackPress = 0;
  timePeriodToExit = 2000;
  loading: HTMLIonLoadingElement | null = null;

  @ViewChildren(IonRouterOutlet) routerOutlets: QueryList<IonRouterOutlet>;
  @ViewChild(IonRouterOutlet) routerOutlet: IonRouterOutlet;

  constructor() {
    addIcons({ cloudOfflineOutline });
    this.watchNetworkStatus();
  }

  ngOnInit(): void {
    this.scheduleBootWatchdog();
    this.initializeApp();
  }

  /**
   * Recovery net for devices whose WebView cold-starts into a blank screen:
   * Angular bootstraps (the index.html splash placeholder is removed) but the
   * first route never attaches to the outlet, leaving an empty white ion-app
   * (seen on a Moto G55 / Android 16 on fresh installs of 4.36). If nothing has
   * rendered after the timeout, reload the WebView once per session; healthy
   * boots make this a no-op.
   */
  private scheduleBootWatchdog(): void {
    const RELOADED_KEY = 'boot-watchdog-reloaded';
    setTimeout(() => {
      const outlet = document.querySelector('ion-router-outlet');
      if (outlet && outlet.childElementCount > 0) return;
      let canRetry = false;
      try {
        // sessionStorage survives location.reload() but not an app restart,
        // bounding this to one automatic retry per WebView session.
        canRetry = sessionStorage.getItem(RELOADED_KEY) !== '1';
        if (canRetry) sessionStorage.setItem(RELOADED_KEY, '1');
      } catch {
        // sessionStorage unavailable — skip rather than risk a reload loop.
      }
      if (canRetry) {
        Sentry.captureMessage('boot-watchdog: initial route not rendered, reloading WebView', 'warning');
        void Sentry.flush(1_500).finally(() => this.reloadWindow());
      }
    }, 10_000);
  }

  /** Indirection for the boot watchdog — jsdom's location.reload cannot be spied on. */
  protected reloadWindow(): void {
    window.location.reload();
  }

  initializeApp() {
    this.platform.ready().then(() => {
      // Before anything renders in earnest: the root font size is derived from
      // what this reads, so measuring late means a visible reflow (TEC-204).
      this.textScale.initialize();
      this.statusBarService.initialize();
      SplashScreen.hide();
      this.appUpdateService.checkForUpdate();
      void this.forceUpdate.check();
      // Register push listeners as early as possible so cold-start
      // notification taps are not lost while auth/network resolve.
      this.notificationService.initPush();
      this.watchAppResume();
      this.purgeLegacyRouteCache();
      // Tarjetas guardadas por versiones anteriores: de Preferences al almacenamiento seguro.
      void this.savedCards.migrateLegacy();

      this.eventService.watch.subscribe((value) => {
        if (value === 'user:logout') {
          this.auth.logout();
        }
        if (value === 'loading:present') {
          this.loadingService.present();
        }
        if (value === 'loading:dismiss') {
          this.loadingService.dismiss();
        }
        if (value === 'back:enable') {
          this.backButtonEvent2();
        }
        if (value === 'transportec:subscribed') {
          // Refresh cached subscription data (slots/current/list) so a just-subscribed
          // period stops showing as subscribable instead of waiting out the query staleTime.
          // Invalidate via QueryClient directly (not SuscriptionStore) to avoid instantiating
          // that store — and firing its unguarded queries — at app boot before auth.
          this.queryClient.invalidateQueries({ queryKey: ['suscription'] });
        }
      });
    });
  }

  /**
   * TEC-215: al volver a foreground se invalida la bandeja de notificaciones sin importar
   * el staleTime (el QueryClient tiene refetchOnWindowFocus apagado). Es la red de seguridad
   * para lo que llegó con el app en background — incluidas las notificaciones que el API
   * crea sin push y los push que no se mostraron por falta de permisos. Sin umbral de
   * tiempo: el caso típico es ver el push en la pantalla bloqueada y abrir el app a los
   * pocos segundos (QA del PR #373), y ahí `foregroundWillDisplay` no corre. Se invalida por
   * llave y no vía NotificationsStore para no instanciar el store (ni su query) en el boot;
   * si nadie observa la query todavía, esto no pide nada.
   *
   * Lo mismo con el chat de un ticket: el SO suspende el socket en background y la respuesta
   * de soporte que llegó mientras tanto sólo está en el servidor. Se relee el hilo que esté
   * montado (el abierto o uno en el stack); el resto sólo queda viejo.
   *
   * Y el saldo del Wallet: las recargas OXXO/SPEI las confirma un webhook y los cobros por
   * bajada los hace el conductor, las dos cosas con el app quizá en background.
   */
  private watchAppResume() {
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        this.queryClient.invalidateQueries({ queryKey: notificationKeys.all });
        this.queryClient.invalidateQueries({ queryKey: supportTicketKeys.threads() });
        this.queryClient.invalidateQueries({ queryKey: balanceKeys.all });
        void this.forceUpdate.check();
      }
    });
  }

  backButtonEvent() {
    this.platform.backButton.subscribeWithPriority(0, () => {
      this.routerOutlets.forEach((outlet: IonRouterOutlet) => {
        /*if (this.router.url != '/tabs/tab1') {
          await this.router.navigate(['/tabs/tab1']);
        } else*/
        alert(this.router.url);
        if (this.router.url === '/tecservice/home') {
          this.auth.askExit();
        }
      });
    });
  }

  // active hardware back button
  backButtonEvent2() {
    this.platform.backButton.subscribeWithPriority(0, async () => {
      // close action sheet
      try {
        const element = await this.actionSheetCtrl.getTop();
        if (element) {
          element.dismiss();
          return;
        }
      } catch (error) {
        /* Intentionally ignore: element already dismissed */
      }

      // close popover
      try {
        const element = await this.popoverCtrl.getTop();
        if (element) {
          element.dismiss();
          return;
        }
      } catch (error) {
        /* Intentionally ignore: element already dismissed */
      }

      // close modal
      try {
        const element = await this.modalCtrl.getTop();
        if (element) {
          element.dismiss();
          return;
        }
      } catch (error) {
        console.log(error);
      }

      this.routerOutlets.forEach(async (outlet: IonRouterOutlet) => {
        if (outlet && outlet.canGoBack()) {
          outlet.pop();
        } else {
          if (new Date().getTime() - this.lastTimeBackPress < this.timePeriodToExit) {
            // navigator['app'].exitApp(); // work for ionic 4
          } else {
            const toast3 = await this.toastController.create({
              message: 'Pulse atrás nuevamente para salir de la App.',
              duration: 2000,
              position: 'middle',
            });
            toast3.present();
            this.lastTimeBackPress = new Date().getTime();
          }
        }
      });
    });
  }

  /**
   * One-time cleanup of legacy on-device Google Routes API cache entries.
   * Polylines are now served by the backend; old `routes_*` keys would otherwise linger forever.
   */
  private async purgeLegacyRouteCache(): Promise<void> {
    try {
      const prefix = (environment.storagePrefix || '') + 'routes_';
      await this.storage.removeKeysWithPrefix(prefix);
    } catch (error) {
      console.warn('Failed to purge legacy route cache:', error);
    }
  }

  private watchNetworkStatus(): void {
    effect(async () => {
      const connected = this.network.isOnline();

      if (!connected) {
        if (this.disconnectedToast) return;
        this.disconnectedToast = await this.toastController.create({
          message: 'Sin conexión a internet',
          duration: 0,
          position: 'top',
          color: 'danger',
          icon: 'cloud-offline-outline',
        });
        await this.disconnectedToast.present();
      } else {
        if (this.disconnectedToast) {
          await this.disconnectedToast.dismiss();
          this.disconnectedToast = null;
        }
      }
    });
  }
}
